import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, Outlet } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ConfigProvider } from 'antd';
import useAuthStore from './store/useAuthStore';

// --- Layout & Auth ---
import LoginPage from './pages/auth/LoginPage';
import AppLayout from './components/layout/AppLayout';
import DashboardPage from './pages/DashboardPage';

// --- Masters ---
import ReadyProductPage from './pages/masters/ReadyProductPage';
import RawMaterialPage from './pages/masters/RawMaterialPage';
import MappingPage from './pages/masters/MappingPage';
import VendorPage from './pages/masters/VendorPage';
import UnitMasterPage from './pages/masters/UnitMasterPage';

// --- Transactions ---
import RawPurchasePage from './pages/transactions/RawPurchasePage';
import ProductionEntryPage from './pages/transactions/ProductionEntryPage';

// --- Admin ---
import UserManagementPage from './pages/admin/UserManagementPage';
import UserRolePage from './pages/admin/UserRolePage';

// --- Reports (Newly Added Real Imports) ---
import StockSummaryPage from './pages/reports/StockSummaryPage';
import PurchaseRegisterPage from './pages/reports/PurchaseRegisterPage';
import ProductionRegisterPage from './pages/reports/ProductionRegisterPage';
import ConsumptionReportPage from './pages/reports/ConsumptionReportPage';

// Create a client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
});

// Protected route wrapper
const ProtectedRoute = () => {
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  return <Outlet />; 
};

function App() {
  const themeColor = useAuthStore((state) => state.user?.themeColor) || '#1a56db';

  return (
    <QueryClientProvider client={queryClient}>
      <ConfigProvider
        theme={{
          token: {
            colorPrimary: themeColor,
            borderRadius: 8,
            fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
          },
        }}
      >
        <BrowserRouter>
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            
            <Route element={<ProtectedRoute />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              
              <Route path="/dashboard" element={<AppLayout />}>
                <Route index element={<DashboardPage />} />

                {/* Masters */}
                
                <Route path="masters/units" element={<UnitMasterPage />} />
                <Route path="masters/ready-products" element={<ReadyProductPage />} />
                <Route path="masters/raw-materials" element={<RawMaterialPage />} />
                <Route path="masters/mappings" element={<MappingPage />} />
                <Route path="masters/vendors" element={<VendorPage />} />

                {/* Transactions */}
                <Route path="transactions/purchase" element={<RawPurchasePage />} />
                <Route path="transactions/production" element={<ProductionEntryPage />} />

                {/* Reports (Now linked to real components) */}
                <Route path="reports/stock" element={<StockSummaryPage />} />
                <Route path="reports/purchase" element={<PurchaseRegisterPage />} />
                <Route path="reports/production" element={<ProductionRegisterPage />} />
                <Route path="reports/consumption" element={<ConsumptionReportPage />} />

                {/* Admin */}
                <Route path="admin/users" element={<UserManagementPage />} />
                <Route path="admin/roles" element={<UserRolePage />} />
              </Route>
            </Route>
            
            <Route path="*" element={<Navigate to="/login" replace />} />
          </Routes>
        </BrowserRouter>
      </ConfigProvider>
    </QueryClientProvider>
  );
}

export default App;